import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  useAttendance,
  useEmployees,
  useLeave,
  usePayrollPolicies,
  useSettings,
  useShifts,
} from '../../../services/httpServices/queries';
import type { Attendance, Leave, Shift } from '../../../services/httpServices/hrService';
import { downloadTextFile } from '../../../utils/csv';
import { formatDateWithWeekday } from '../../../utils/dateFormat';
import { DatePicker } from '../../../components/ui/DatePicker';
import {
  extractTime,
  findShift,
  formatShiftTime,
  formatWeeklyOffDays,
  isDateInRange,
  isWeeklyOffDay,
  resolveWeeklyOffDays,
} from './utils';
import { X, Printer, Download, Loader2, AlertCircle, CalendarDays } from 'lucide-react';

interface DailyRosterPrintModalProps {
  onClose: () => void;
  /** Date to open the preview on, as `YYYY-MM-DD`. Defaults to today. */
  initialDate?: string;
}

interface DutyEntry {
  employee: any;
  record?: Attendance;
}

interface ShiftGroup {
  key: string;
  name: string;
  start?: string | null;
  end?: string | null;
  order: number;
  rows: DutyEntry[];
}

interface LeaveEntry {
  employee: any;
  leave: Leave;
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function employeeName(employee: any): string {
  return (
    `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() ||
    employee?.name ||
    'Employee'
  );
}

function compareByName(a: any, b: any): number {
  return employeeName(a).localeCompare(employeeName(b));
}

/** Shift referenced by an attendance record: joined relation, then id, then code/name. */
function resolveShift(shifts: Shift[], record?: Attendance): Shift | undefined {
  if (!record) return undefined;
  if (record.shift) return record.shift;
  const byId = findShift(shifts, record.shiftId);
  if (byId) return byId;
  return shifts.find((s) => s.shiftCode === record.shiftType || s.name === record.shiftType);
}

/**
 * Self-contained styles for the notice-board sheet. Shared by the on-screen
 * preview, the print window and the downloaded HTML file so all three match.
 */
const SHEET_CSS = `
.dr-sheet { color:#111827; font-family:Arial,Helvetica,sans-serif; font-size:12px; background:#ffffff; padding:16px; }
.dr-sheet * { box-sizing:border-box; }
.dr-header { text-align:center; border-bottom:2px solid #111827; padding-bottom:8px; margin-bottom:10px; }
.dr-hospital { font-size:20px; font-weight:700; }
.dr-title { font-size:14px; font-weight:700; margin-top:3px; letter-spacing:2px; text-transform:uppercase; }
.dr-date { font-size:12px; font-weight:600; margin-top:4px; }
.dr-meta { font-size:11px; color:#374151; margin-top:5px; display:flex; justify-content:center; gap:16px; flex-wrap:wrap; }
.dr-summary { display:flex; gap:10px; margin:10px 0 4px; }
.dr-stat { flex:1; border:1px solid #d1d5db; border-radius:4px; padding:6px 8px; text-align:center; }
.dr-stat-label { font-size:9px; text-transform:uppercase; letter-spacing:.5px; color:#6b7280; }
.dr-stat-value { font-size:16px; font-weight:700; margin-top:1px; }
.dr-section { margin-top:12px; }
.dr-section-title { display:flex; align-items:baseline; justify-content:space-between; gap:8px; font-size:12px; font-weight:700; background:#e5e7eb; padding:5px 8px; border-left:3px solid #0f766e; page-break-after:avoid; }
.dr-section-time { font-weight:400; font-size:11px; color:#374151; }
.dr-table { width:100%; border-collapse:collapse; margin-top:6px; }
.dr-table th, .dr-table td { border:1px solid #d1d5db; padding:5px 7px; text-align:left; vertical-align:middle; }
.dr-table thead { display:table-header-group; }
.dr-table tr { page-break-inside:avoid; }
.dr-table th { background:#f9fafb; font-size:10px; text-transform:uppercase; letter-spacing:.4px; text-align:center; }
.dr-serial { width:36px; text-align:center; font-weight:700; }
.dr-name { font-weight:600; }
.dr-code { font-size:10px; color:#6b7280; }
.dr-mobile { width:120px; }
.dr-sign { width:90px; }
.dr-empty { padding:28px; text-align:center; color:#6b7280; }
.dr-leave .dr-section-title { border-left-color:#dc2626; }
.dr-off .dr-section-title { border-left-color:#d97706; }
.dr-footer { margin-top:16px; padding-top:6px; border-top:1px solid #d1d5db; font-size:10px; color:#6b7280; display:flex; justify-content:space-between; gap:8px; }
`;

export function DailyRosterPrintModal({ onClose, initialDate }: DailyRosterPrintModalProps) {
  const [date, setDate] = useState(() => initialDate || toDateKey(new Date()));
  const [departmentId, setDepartmentId] = useState('');

  const { data: employeesData, isLoading: employeesLoading, isError: employeesError } = useEmployees(
    1,
    500,
    { status: 'active' },
  );
  const { data: attendanceData, isLoading: attendanceLoading } = useAttendance(undefined, date, date);
  const { data: leaveData, isLoading: leaveLoading } = useLeave(undefined, 'APPROVED', date, date);
  const { data: shiftsData, isLoading: shiftsLoading } = useShifts();
  const { data: payrollPoliciesData, isLoading: payrollPoliciesLoading } = usePayrollPolicies();
  const { data: settingsData } = useSettings();

  const employees = useMemo(() => employeesData?.data || [], [employeesData]);
  const shifts = useMemo(() => shiftsData?.data || [], [shiftsData]);
  const payrollPolicies = useMemo(() => payrollPoliciesData?.data || [], [payrollPoliciesData]);

  const weeklyOffDaysByEmployee = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const employee of employees as any[]) {
      map.set(employee.id, resolveWeeklyOffDays(employee, payrollPolicies));
    }
    return map;
  }, [employees, payrollPolicies]);

  const hospitalName =
    settingsData?.data?.find((setting) => setting.key === 'hospital_name_en')?.value?.trim() ||
    settingsData?.data?.find((setting) => setting.key === 'hospital_name')?.value?.trim() ||
    'Hospital';

  // Department filter options are derived from the active employees so no extra
  // request is needed and only departments that can appear are listed.
  const departments = useMemo(() => {
    const map = new Map<string, string>();
    for (const employee of employees) {
      const name = employee?.department?.name;
      if (employee?.departmentId && name) map.set(employee.departmentId, name);
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [employees]);

  const visibleEmployees = useMemo(
    () => (departmentId ? employees.filter((e: any) => e.departmentId === departmentId) : employees),
    [employees, departmentId],
  );

  const attendanceByEmployee = useMemo(() => {
    const map = new Map<string, Attendance>();
    for (const record of attendanceData?.data || []) {
      const dateKey =
        typeof record.date === 'string' ? record.date.slice(0, 10) : toDateKey(new Date(record.date));
      if (dateKey === date) map.set(record.employeeId, record);
    }
    return map;
  }, [attendanceData, date]);

  const leaveByEmployee = useMemo(() => {
    const map = new Map<string, Leave>();
    for (const leave of leaveData?.data || []) {
      if (isDateInRange(date, leave.startDate, leave.endDate)) {
        map.set(leave.employeeId, leave);
      }
    }
    return map;
  }, [leaveData, date]);

  const { shiftGroups, leaveEntries, offDayEntries, notScheduled, onDutyCount, totalCount } = useMemo(() => {
    const groups = new Map<string, ShiftGroup>();
    const leaves: LeaveEntry[] = [];
    const offDays: any[] = [];
    let scheduled = 0;
    let onDuty = 0;

    for (const employee of visibleEmployees) {
      const leave = leaveByEmployee.get(employee.id);
      if (leave) {
        leaves.push({ employee, leave });
        continue;
      }

      const record = attendanceByEmployee.get(employee.id);
      const employeeOffDays = weeklyOffDaysByEmployee.get(employee.id) || [];

      // A paid weekly off with no assigned shift is off duty, not "missing".
      if (!record && isWeeklyOffDay(date, employeeOffDays)) {
        offDays.push(employee);
        continue;
      }

      const shift = resolveShift(shifts, record);

      // Nobody is on duty for the day unless a shift (or at least an attendance
      // record) exists — those staff stay off the notice board.
      if (!shift && !record) {
        scheduled += 1;
        continue;
      }

      const key = shift?.id || '__other__';
      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          name: shift?.name || record?.shiftType || 'Other Duty',
          start: shift ? formatShiftTime(shift.startTime) : extractTime(record?.checkIn),
          end: shift ? formatShiftTime(shift.endTime) : extractTime(record?.checkOut),
          order: shift?.displayOrder ?? 999,
          rows: [],
        };
        groups.set(key, group);
      }
      group.rows.push({ employee, record });
      onDuty += 1;
    }

    const sortedGroups = Array.from(groups.values())
      .map((group) => ({
        ...group,
        rows: [...group.rows].sort((a, b) => compareByName(a.employee, b.employee)),
      }))
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return (a.start || '99:99').localeCompare(b.start || '99:99');
      });

    return {
      shiftGroups: sortedGroups,
      leaveEntries: [...leaves].sort((a, b) => compareByName(a.employee, b.employee)),
      offDayEntries: [...offDays].sort(compareByName),
      notScheduled: scheduled,
      onDutyCount: onDuty,
      totalCount: visibleEmployees.length,
    };
  }, [visibleEmployees, attendanceByEmployee, leaveByEmployee, shifts, weeklyOffDaysByEmployee, date]);

  const selectedDepartmentName = departmentId
    ? departments.find((department) => department.id === departmentId)?.name
    : undefined;

  const isLoading =
    employeesLoading || attendanceLoading || leaveLoading || shiftsLoading || payrollPoliciesLoading;

  const buildStandaloneHtml = () => {
    const sheet = document.getElementById('daily-roster-print-sheet');
    if (!sheet) return '';
    return (
      `<!doctype html><html><head><meta charset="utf-8" /><title>Daily Duty Roster</title>` +
      `<style>${SHEET_CSS}` +
      `@page{size:A4;margin:10mm;}` +
      `@media print{body{margin:0;}.dr-sheet{padding:0;}}` +
      `</style></head><body>${sheet.outerHTML}</body></html>`
    );
  };

  const handlePrint = () => {
    const html = buildStandaloneHtml();
    if (!html) return;

    const printWindow = window.open('', '_blank', 'width=900,height=1000');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the daily roster');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 350);
  };

  const handleDownload = () => {
    const html = buildStandaloneHtml();
    if (!html) return;
    downloadTextFile(`Daily-Duty-Roster-${date}.html`, html, 'text/html;charset=utf-8;');
    toast.success('Daily duty roster downloaded');
  };

  const hasContent = shiftGroups.length > 0 || leaveEntries.length > 0 || offDayEntries.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <style>{SHEET_CSS}</style>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <CalendarDays size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">Daily Duty Roster</h2>
              <p className="text-sm text-text-muted">
                Download or print a one-day notice-board roster grouped by shift.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Date</label>
              <DatePicker value={date} onChange={setDate} />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Department</label>
              <select
                value={departmentId}
                onChange={(event) => setDepartmentId(event.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All departments</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto bg-background p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : employeesError ? (
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load employees for the roster.</span>
            </div>
          ) : (
            <div className="mx-auto max-w-[210mm] shadow-sm border border-border rounded-lg overflow-hidden">
              <div id="daily-roster-print-sheet" className="dr-sheet">
                <div className="dr-header">
                  <div className="dr-hospital">{hospitalName}</div>
                  <div className="dr-title">Daily Duty Roster</div>
                  <div className="dr-date">{formatDateWithWeekday(date)}</div>
                  <div className="dr-meta">
                    {selectedDepartmentName && <span>Department: {selectedDepartmentName}</span>}
                    <span>Total staff: {totalCount}</span>
                  </div>
                </div>

                <div className="dr-summary">
                  <div className="dr-stat">
                    <div className="dr-stat-label">On Duty</div>
                    <div className="dr-stat-value">{onDutyCount}</div>
                  </div>
                  <div className="dr-stat">
                    <div className="dr-stat-label">On Leave</div>
                    <div className="dr-stat-value">{leaveEntries.length}</div>
                  </div>
                  <div className="dr-stat">
                    <div className="dr-stat-label">Weekly Off</div>
                    <div className="dr-stat-value">{offDayEntries.length}</div>
                  </div>
                  <div className="dr-stat">
                    <div className="dr-stat-label">Not Scheduled</div>
                    <div className="dr-stat-value">{notScheduled}</div>
                  </div>
                </div>

                {!hasContent && (
                  <div className="dr-empty">
                    No duty assignments, approved leave or weekly off staff for this date.
                  </div>
                )}

                {shiftGroups.map((group) => (
                  <div key={group.key} className="dr-section">
                    <div className="dr-section-title">
                      <span>
                        {group.name}
                        {group.start && group.end ? ` (${group.start} – ${group.end})` : ''}
                      </span>
                      <span className="dr-section-time">
                        {group.rows.length} on duty
                      </span>
                    </div>
                    <table className="dr-table">
                      <thead>
                        <tr>
                          <th className="dr-serial">#</th>
                          <th>Employee</th>
                          <th>Designation</th>
                          <th>Department</th>
                          <th className="dr-mobile">Mobile</th>
                          <th className="dr-sign">Sign</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((entry, index) => (
                          <tr key={entry.employee.id}>
                            <td className="dr-serial">{index + 1}</td>
                            <td>
                              <div className="dr-name">{employeeName(entry.employee)}</div>
                              {entry.employee?.employeeCode && (
                                <div className="dr-code">{entry.employee.employeeCode}</div>
                              )}
                            </td>
                            <td>{entry.employee?.designation?.name || '—'}</td>
                            <td>{entry.employee?.department?.name || '—'}</td>
                            <td className="dr-mobile">{entry.employee?.mobileNumber || '—'}</td>
                            <td className="dr-sign" />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

                {leaveEntries.length > 0 && (
                  <div className="dr-section dr-leave">
                    <div className="dr-section-title">
                      <span>On Leave</span>
                      <span className="dr-section-time">{leaveEntries.length} staff</span>
                    </div>
                    <table className="dr-table">
                      <thead>
                        <tr>
                          <th className="dr-serial">#</th>
                          <th>Employee</th>
                          <th>Department</th>
                          <th>Leave Type</th>
                          <th>Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaveEntries.map((entry, index) => (
                          <tr key={entry.employee.id}>
                            <td className="dr-serial">{index + 1}</td>
                            <td>
                              <div className="dr-name">{employeeName(entry.employee)}</div>
                              {entry.employee?.employeeCode && (
                                <div className="dr-code">{entry.employee.employeeCode}</div>
                              )}
                            </td>
                            <td>{entry.employee?.department?.name || '—'}</td>
                            <td>{entry.leave.type || 'Leave'}</td>
                            <td>{formatDateWithWeekday(entry.leave.startDate)} – {formatDateWithWeekday(entry.leave.endDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {offDayEntries.length > 0 && (
                  <div className="dr-section dr-off">
                    <div className="dr-section-title">
                      <span>Weekly Off</span>
                      <span className="dr-section-time">{offDayEntries.length} staff</span>
                    </div>
                    <table className="dr-table">
                      <thead>
                        <tr>
                          <th className="dr-serial">#</th>
                          <th>Employee</th>
                          <th>Department</th>
                          <th>Weekly Off Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offDayEntries.map((employee, index) => (
                          <tr key={employee.id}>
                            <td className="dr-serial">{index + 1}</td>
                            <td>
                              <div className="dr-name">{employeeName(employee)}</div>
                              {employee?.employeeCode && (
                                <div className="dr-code">{employee.employeeCode}</div>
                              )}
                            </td>
                            <td>{employee?.department?.name || '—'}</td>
                            <td>
                              {formatWeeklyOffDays(
                                weeklyOffDaysByEmployee.get(employee.id) || [],
                              ) || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="dr-footer">
                  <span>{hospitalName}</span>
                  <span>Notice board copy — generated from FastAid OS.</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border">
          <span className="text-xs text-text-muted">
            {isLoading
              ? 'Loading roster…'
              : `${onDutyCount} on duty · ${leaveEntries.length} on leave · ${offDayEntries.length} weekly off · ${notScheduled} not scheduled`}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleDownload}
              disabled={isLoading || !hasContent}
              className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Download size={16} />
              Download
            </button>
            <button
              onClick={handlePrint}
              disabled={isLoading || !hasContent}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Printer size={16} />
              Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
